<template>
    <div class="container-fluid">
        <div class="row justify-content-center">
            <div class="col-12 admin-table-col">
                <!-- Empty Display -->
                <div v-if="noData && !loading">
                    <br />
                    <br />
                    <br />
                    <div class="d-flex justify-content-center mb-5">
                        <img src="/static/general/img/undraw_no_data.svg" height="180" />
                    </div>
                    <div class="d-flex justify-content-center mb-4">
                        <div class="text-center">
                            <h1 class="h5 font-weight-bolder mb-3 pb-1">No pages created yet.</h1>
                            <p class="text-center text-secondary mb-2">
                                Create customized landing pages to give learners access to multiple learning experiences via a
                                single link.
                            </p>
                        </div>
                    </div>
                    <div>
                        <div class="d-flex justify-content-center mb-4">
                            <button @click="openCreate()" type="button" class="btn btn-primary">+ Create a Page</button>
                        </div>
                    </div>
                    <div class="d-flex justify-content-center">
                        <p class="text-center text-secondary small mt-2">
                            Need a walkthrough? Let us know at
                            <a href="mailto:support@affinitylearning.ca?subject=Walkthrough Request" target="_blank">
                                support@affinitylearning.ca</a
                            >.
                        </p>
                    </div>
                </div>

                <!-- Non-Empty Header -->
                <div v-show="libraryItems.length > 0">
                    <br />
                    <br />
                    <div class="d-flex pb-4 mt-3">
                        <div class="flex-grow-1 align-self-center">
                            <h1 class="h4 mb-2 font-weight-bolder">
                                <span>Pages</span>
                            </h1>
                            <p class="mb-0 text-secondary medium">
                                Create pages of multiple learning experiences, and share them as links.
                            </p>
                        </div>
                        <div class="flex-shrink-0 align-self-center">
                            <button @click="openCreate()" type="button" class="btn btn-sm btn-primary ml-5">
                                <span class="icon icon-glyphicons-basic-plus icon-mr"></span>
                                <span>Create</span>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- List Display -->
                <div v-if="!noData">
                    <ul class="list-group">
                        <li v-for="item in libraryItems" :key="item.id" class="list-group-item py-3">
                            <div class="d-md-flex">
                                <div class="flex-grow-1 align-self-center pr-md-5">
                                    <p class="mb-1">{{ item.page.name }}</p>
                                    <p class="text-secondary small mb-1">
                                        Last modified on {{ formatDate(item.page.last_modified) }} by
                                        {{ item.page.last_modified_by.email }}
                                    </p>
                                    <div class="small">
                                        <span class="badge badge-primary case-badge mr-1"
                                            >{{ item.page.view_count }} Views</span
                                        >
                                        <button
                                            class="btn badge case-badge mr-1 border-0"
                                            @click="openEdit(item)"
                                            :class="{
                                                'btn-info': item.page.is_enabled,
                                                'btn-warning': !item.page.is_enabled,
                                            }"
                                        >
                                            <span v-if="item.page.is_enabled">Enabled</span>
                                            <span v-else>Access Disabled</span>
                                        </button>
                                        <span class="text-secondary ml-2" v-show="item.page.share_link.require_passcode"
                                            ><span
                                                class="icon icon-100 icon-glyphicons-basic-lock mr-1 align-middle"
                                            ></span>
                                            <span class="mr-1 align-middle">Passcode:</span>
                                            <span class="font-weight-500 align-middle">{{
                                                item.page.share_link.passcode
                                            }}</span></span
                                        >
                                    </div>
                                </div>
                                <div class="flex-shrink-0 align-self-center mt-3 mt-md-0">
                                    <button @click="openAccess(item)" type="button" class="btn btn-light btn-sm mr-1">
                                        <span class="icon icon-glyphicons-basic-user"></span>
                                    </button>
                                    <div class="dropdown d-inline">
                                        <button
                                            class="btn btn-light btn-sm mr-1"
                                            type="button"
                                            aria-label="More options"
                                            data-toggle="dropdown"
                                        >
                                            <span class="icon icon-lg icon-glyphicons-basic-more-vertical"></span>
                                        </button>
                                        <div class="dropdown-menu dropdown-menu-right shadow-sm medium">
                                            <button @click="openEdit(item)" class="dropdown-item" role="button">
                                                <span
                                                    class="icon icon-glyphicons-basic-cogwheel align-middle mr-1"
                                                ></span>
                                                <span class="align-middle">Settings</span>
                                            </button>
                                            <h6 class="dropdown-header">Actions</h6>
                                            <button @click="openClone(item)" class="dropdown-item" role="button">
                                                <span
                                                    class="icon icon-glyphicons-basic-squares align-middle mr-1"
                                                ></span>
                                                <span class="align-middle">Clone</span>
                                            </button>
                                            <button @click="openRemove(item)" class="dropdown-item" role="button">
                                                <span class="icon icon-glyphicons-basic-bin align-middle mr-1"></span>
                                                <span class="align-middle">Remove</span>
                                            </button>
                                        </div>
                                    </div>
                                    <button @click="openEditLink(item)" type="button" class="btn btn-light btn-sm mr-1">
                                        <span class="icon icon-glyphicons-basic-link mr-1"></span>
                                        <span class="align-middle">View Link</span>
                                    </button>
                                    <router-link
                                        v-if="item.page.share_link.learner_reporting_enabled"
                                        :to="{ name: 'PageReports', params: { pageId: item.page.id } }"
                                        type="button"
                                        class="btn btn-light btn-sm mr-1"
                                    >
                                        <span class="icon icon-glyphicons-basic-bars mr-1"></span>
                                        <span class="align-middle">Reports</span>
                                    </router-link>
                                    <router-link
                                        :to="{ name: 'PageEditor', params: { id: item.page.id } }"
                                        class="btn btn-link btn-sm mr-1"
                                    >
                                        <span class="icon icon-glyphicons-basic-square-edit mr-1"></span>
                                        <span class="align-middle">Edit Page</span>
                                    </router-link>
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- Clone Modal -->
        <base-modal id="clone-modal" :loading="modalLoading" :centered="true">
            <template slot="title">
                <span class="align-middle">Clone Page</span>
            </template>
            <template>
                <div class="row">
                    <div class="col-12">
                        <p class="my-2">
                            Create a clone of <strong>{{ selectedPage.name }}</strong
                            >?
                        </p>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button @click="clone()" :disabled="modalLoading" class="btn btn-primary" type="button">Clone</button>
            </template>
        </base-modal>

        <!-- Create Modal -->
        <base-modal id="create-modal" :loading="modalLoading" :centered="true">
            <template slot="title">
                <span class="align-middle">New Page</span>
            </template>
            <template>
                <div class="row">
                    <div class="col-12">
                        <base-form ref="createForm" v-model="selectedItem.page" :fields="createPageFields"></base-form>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button @click="save()" :disabled="modalLoading" class="btn btn-primary" type="button">Create</button>
            </template>
        </base-modal>

        <!-- Edit Modal -->
        <base-modal id="edit-modal" :loading="modalLoading" :centered="true">
            <template slot="title">
                <span class="align-middle">Edit Page</span>
            </template>
            <template>
                <div class="row">
                    <div class="col-12">
                        <base-form
                            ref="editForm"
                            v-model="selectedItem.page"
                            :fields="editPageFields"
                            :disabled="!isSelectedItemEditor"
                        ></base-form>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="save()"
                    :disabled="modalLoading || !isSelectedItemEditor"
                    class="btn btn-primary"
                    type="button"
                >
                    Save
                </button>
            </template>
        </base-modal>

        <!-- Remove Modal -->
        <base-modal id="remove-modal" :loading="modalLoading" :centered="true">
            <template slot="title">
                <span class="icon icon-100 icon-glyphicons-basic-bin align-middle mr-1"></span>
                <span class="align-middle">Remove Page</span>
            </template>
            <template>
                <div class="row">
                    <div class="col-12">
                        <p v-if="!isSelectedItemOwner" class="my-3 text-secondary text-center">
                            Only admins and owners can remove this item.
                        </p>
                        <p v-else class="my-3">
                            Remove <strong>{{ selectedPage.name }}</strong
                            >?
                        </p>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="remove()"
                    :disabled="!isSelectedItemOwner || modalLoading"
                    class="btn btn-danger"
                    type="button"
                >
                    Remove
                </button>
            </template>
        </base-modal>

        <!-- Access Modal -->
        <base-modal id="access-modal" :loading="accessModalLoading" :centered="true">
            <template slot="title">
                <span class="icon icon-glyphicons-basic-user icon-mr"></span>
                <span>Author Access</span>
            </template>
            <template>
                <author-access-form
                    v-if="selectedItem.id"
                    v-model="selectedItem"
                    :loading.sync="accessModalLoading"
                ></author-access-form>
            </template>
            <template slot="footer">
                <button class="btn btn-gray" type="button" data-dismiss="modal">Done</button>
            </template>
        </base-modal>

        <!-- Link Modal -->
        <publish-page-link-modal
            v-model="selectedItem.page"
            :read-only="!isSelectedItemEditor"
            ref="pageLinkModal"
        ></publish-page-link-modal>

        <!-- Paywall Modal -->
        <base-modal id="cap-reached-modal" :centered="true">
            <template slot="title"> Page Capacity Reached </template>
            <template>
                <div class="d-flex justify-content-center mb-4 pb-2 mt-2">
                    <img src="/static/general/img/undraw_no_data.svg" height="150" alt="" />
                </div>
                <div class="mb-2">
                    <p class="font-weight-500 text-center mb-3">
                        You've reached the Basic Plan limit of
                        <span class="font-weight-bolder">{{ pageCapacity }}</span> pages.
                    </p>
                    <p class="text-center mb-3">
                        To create more pages,
                        <a
                            v-if="userIsOrganizationAdmin"
                            href="#"
                            @click="$refs.billingView.open()"
                            data-dismiss="modal"
                            >upgrade your plan</a
                        >
                        <span v-else>contact your administrator</span>.
                    </p>
                    <support-request-prompt class="text-center" />
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button disabled class="btn btn-primary" type="button">Create</button>
            </template>
        </base-modal>

        <billing-view ref="billingView"></billing-view>
    </div>
</template>

<script>
import http from "@/axios.js"
import BaseModal from "@/components/BaseModal"
import BaseForm from "@/components/BaseForm"
import BillingView from "@/components/BillingView"
import SupportRequestPrompt from "@/components/SupportRequestPrompt"
import PublishPageLinkModal from "@/components/PublishPageLinkModal"
import AuthorAccessForm from "@/components/AuthorAccessForm"
import Utilities from "@/assets/js/utilities.js"

export default {
    name: "PublishPages",
    components: {
        BaseModal,
        BaseForm,
        BillingView,
        PublishPageLinkModal,
        SupportRequestPrompt,
        AuthorAccessForm,
    },
    data() {
        return {
            libraryItems: [],
            selectedItem: {
                page: {},
            },
            isSelectedItemOwner: false,
            isSelectedItemEditor: false,
            modalLoading: false,
            accessModalLoading: false,
        }
    },
    computed: {
        loading() {
            return this.$store.getters.loading
        },
        noData() {
            return this.libraryItems.length === 0
        },
        currentOrganization() {
            return this.$store.getters.currentOrganization
        },
        currentOrganizationDetails() {
            return this.$store.getters.organizationDetails
        },
        pageCapacity() {
            return this.$store.getters.organizationDetails.max_pages
        },
        userIsOrganizationAdmin() {
            return this.$store.getters.userIsOrganizationAdmin
        },
        editPageFields() {
            return [
                {
                    name: "Internal Label",
                    key: "name",
                    type: "text",
                    required: true,
                    subtext: "An internal label to identify this page to other authors.",
                },
                {
                    type: "divider",
                    name: "Access",
                },
                {
                    name: "Enable Access",
                    type: "switch",
                    key: "is_enabled",
                    subtext: "Turn this switch off to prevent this page from being viewed.",
                },
            ]
        },
        createPageFields() {
            return [
                {
                    name: "Internal Label",
                    key: "name",
                    type: "text",
                    required: true,
                    subtext: "An internal label to identify this page to other authors.",
                },
            ]
        },
        selectedPage() {
            if (this.selectedItem) {
                return this.selectedItem.page
            }
            return {}
        },
    },
    methods: {
        formatDate(date) {
            return Utilities.formatDateTime(date)
        },
        fetchData() {
            this.$store.dispatch("addLoad")
            let vm = this
            http.get("/api/library-items/", {
                params: {
                    organizationId: this.organizationId,
                    pageStart: 0,
                    pageEnd: 500,
                    itemTypes: "page",
                },
            })
                .then(function (response) {
                    vm.libraryItems = response.data.results
                })
                .finally(() => vm.$store.dispatch("removeLoad"))
        },
        openCreate() {
            if (this.pageCapacity !== null && this.libraryItems.length >= this.pageCapacity) {
                $("#cap-reached-modal").modal("show")
                return
            }
            this.selectedItem = {
                page: {
                    label: null,
                    passcode: null,
                    identification_method: "none",
                    require_passcode: false,
                    learner_reporting_enabled: false,
                },
            }
            this.$refs.editForm.resetValidate()
            $("#create-modal").modal("show")
        },
        openEdit(item) {
            // Create an editable copy of the item.
            this.selectedItem = Object.assign({}, item)
            this.selectedItem.page = Object.assign({}, item.page)
            this.$refs.editForm.resetValidate()
            this.checkItemEditAccess()
            $("#edit-modal").modal("show")
        },
        openRemove(item) {
            this.selectedItem = item
            this.checkItemOwnership()
            $("#remove-modal").modal("show")
        },
        openAccess(item) {
            this.selectedItem = item
            $("#access-modal").modal("show")
        },
        openClone(item) {
            this.selectedItem = item
            $("#clone-modal").modal("show")
        },
        openEditLink(item) {
            this.selectedItem = item
            this.checkItemEditAccess()
            this.$refs.pageLinkModal.open()
        },
        save() {
            if (this.selectedItem.id) {
                this.update()
            } else {
                this.create()
            }
        },
        create() {
            // Check form validity.
            if (!this.$refs.createForm.validate()) {
                return
            }
            this.modalLoading = true
            let vm = this
            http.post("/api/library-items/", {
                ...this.selectedItem,
                item_type: "page",
                organizationId: this.organizationId,
            })
                .then(function (response) {
                    $("#create-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Page created.",
                        data: {},
                    })
                    vm.$router.push({ name: "PageEditor", params: { id: response.data.page.id } })
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        update() {
            if (!this.$refs.editForm.validate()) {
                return
            }
            this.modalLoading = true
            let vm = this
            http.put(`/api/pages/${this.selectedPage.id}/`, this.selectedPage)
                .then(() => {
                    $("#edit-modal").modal("hide")
                    vm.fetchData()
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Page saved.",
                        data: {},
                    })
                })
                .finally(() => {
                    vm.modalLoading = false
                })
        },
        remove() {
            this.modalLoading = true
            let vm = this
            http.put(`/api/library-items/${this.selectedItem.id}/remove/`)
                .then(function () {
                    $("#remove-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Page removed.",
                        data: {},
                    })
                    vm.fetchData()
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        clone() {
            this.modalLoading = true
            let vm = this
            http.post(`/api/library-items/${this.selectedItem.id}/clone/`)
                .then(function () {
                    $("#clone-modal").modal("hide")
                    vm.fetchData()
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Page cloned.",
                        data: {},
                    })
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        checkItemOwnership() {
            this.isSelectedItemOwner = false
            let vm = this
            this.checkLibraryItemAccess("owner").then((hasAccess) => {
                vm.isSelectedItemOwner = hasAccess
            })
        },
        checkItemEditAccess() {
            let vm = this
            this.checkLibraryItemAccess("editor").then((hasAccess) => {
                vm.isSelectedItemEditor = hasAccess
            })
        },
        // Check whether the requester has a given level of access to the library item.
        checkLibraryItemAccess(accessType) {
            this.isSelectedItemOwner = false
            return new Promise((resolve) => {
                http.get(`/api/library-items/${this.selectedItem.id}/check-permissions/?accessType=${accessType}`).then(
                    (response) => {
                        resolve(response.data.has_permission)
                    }
                )
            })
        },
    },
    created() {
        this.fetchData()
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
</style>
